import { useMemo, useState } from "react";
import {
  Alert,
  Button,
  Input,
  Modal,
  Select,
  Steps,
  Table,
  Tag,
  Tooltip,
} from "antd";
import { useMutation } from "@tanstack/react-query";

import { useScopeParams } from "@/hooks/useScopeParams";
import { useAppNotification } from "@/hooks/useAppNotification";
import ruleStudioApi from "@/services/ruleStudioApi";
import type {
  RuleDefinition,
  RuleLevel,
  RuleSeverity,
  TestResult,
} from "./ruleStudio.types";

const { TextArea } = Input;

const LEVELS: RuleLevel[] = ["Participant", "Site", "Trial"];
const SEVERITIES: RuleSeverity[] = ["Critical", "Major", "Minor"];
const DOMAINS = [
  "Visits / Consent",
  "Adverse Events",
  "Labs",
  "Dosing",
  "Enrollment",
  "Imaging",
  "Other",
];

const PROMPT_EXAMPLES = [
  "any completed visit dated before the participant signed informed consent",
  "SAEs flagged as fatal that have no matching death record",
];

type RuleWizardProps = {
  open: boolean;
  onClose: () => void;
  onSaved: (rule: RuleDefinition) => void;
};

/**
 * Three-step authoring flow for deterministic discrepancy rules:
 * Describe → Logic & test (generate/edit/refine SQL, run it, inspect the
 * actual matching records) → Review & activate. AI only ever proposes SQL;
 * nothing is saved without a fresh passing test run. The draft rule name and
 * domain are inferred from the description and stay editable.
 */
export default function RuleWizard({
  open,
  onClose,
  onSaved,
}: RuleWizardProps) {
  const { id } = useScopeParams();
  const { openSuccessNotification, contextHolder } = useAppNotification();

  const [step, setStep] = useState(0);
  const [name, setName] = useState("");
  const [level, setLevel] = useState<RuleLevel>("Participant");
  const [domain, setDomain] = useState<string>("Other");
  const [severity, setSeverity] = useState<RuleSeverity>("Major");
  const [prompt, setPrompt] = useState("");
  const [sql, setSql] = useState("");
  const [sqlVersion, setSqlVersion] = useState(1);
  const [explanation, setExplanation] = useState("");
  const [tables, setTables] = useState<string[]>([]);
  const [refinement, setRefinement] = useState("");
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [testedSql, setTestedSql] = useState<string | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);

  const resultsStale = testResult !== null && testedSql !== sql;
  const hasFreshTest = testResult !== null && !resultsStale;

  const reset = () => {
    setStep(0);
    setName("");
    setLevel("Participant");
    setDomain("Other");
    setSeverity("Major");
    setPrompt("");
    setSql("");
    setSqlVersion(1);
    setExplanation("");
    setTables([]);
    setRefinement("");
    setTestResult(null);
    setTestedSql(null);
    setErrorText(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const generateMutation = useMutation({
    mutationFn: () => ruleStudioApi.generate(id, { prompt, level }),
    onSuccess: (result) => {
      setSql(result.sql);
      setSqlVersion(1);
      setExplanation(result.explanation);
      setTables(result.tables);
      if (result.name) setName(result.name);
      if (result.domain) setDomain(result.domain);
      setTestResult(null);
      setTestedSql(null);
      setErrorText(null);
      setStep(1);
    },
    onError: (error: Error) => setErrorText(error.message),
  });

  const refineMutation = useMutation({
    mutationFn: () =>
      ruleStudioApi.generate(id, {
        prompt,
        level,
        domain,
        current_sql: sql,
        refinement,
      }),
    onSuccess: (result) => {
      setSql(result.sql);
      setSqlVersion((v) => v + 1);
      setExplanation(result.explanation);
      setTables(result.tables);
      setRefinement("");
      setErrorText(null);
    },
    onError: (error: Error) => setErrorText(error.message),
  });

  const testMutation = useMutation({
    mutationFn: () => ruleStudioApi.testSql(id, { sql_text: sql, level }),
    onSuccess: (result) => {
      setTestResult(result);
      setTestedSql(sql);
      setErrorText(null);
    },
    onError: (error: Error) => {
      setErrorText(error.message);
      setTestResult(null);
      setTestedSql(null);
    },
  });

  const saveMutation = useMutation({
    mutationFn: (activate: boolean) =>
      ruleStudioApi.createRule(id, {
        name,
        description: prompt,
        level,
        domain,
        severity,
        sql_text: sql,
        explanation,
        activate,
      }),
    onSuccess: (rule, activate) => {
      openSuccessNotification({
        title: (
          <p className="text-[#003832] text-base font-semibold">
            {activate ? "Rule activated" : "Rule saved as draft"}
          </p>
        ),
        description: activate
          ? `"${rule.name}" ran against current trial data — ${
              rule.activation_run?.new_count ?? 0
            } discrepancies created as NEW.`
          : `"${rule.name}" was added to the rule library.`,
      });
      onSaved(rule);
      handleClose();
    },
    onError: (error: Error) => setErrorText(error.message),
  });

  const resultColumns = useMemo(() => {
    if (!testResult) return [];
    return testResult.columns.map((column, index) => ({
      title: column,
      key: column,
      dataIndex: index,
      ellipsis: true,
      render: (value: unknown) =>
        value === null || value === undefined || value === "" ? (
          "-"
        ) : (
          <span
            className={
              column === "subject_id" || column.endsWith("_id")
                ? "font-mono text-xs"
                : undefined
            }
          >
            {String(value)}
          </span>
        ),
    }));
  }, [testResult]);

  const describeStep = (
    <div className="max-w-[760px]">
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <p className="text-xs font-semibold text-gray-600 mb-1">Level</p>
          <Select
            className="w-full"
            value={level}
            onChange={setLevel}
            options={LEVELS.map((l) => ({ value: l, label: l }))}
          />
          <p className="text-[11px] text-gray-400 mt-1">
            Rules are record-wise: one discrepancy per matching record.
          </p>
        </div>
        <div>
          <p className="text-xs font-semibold text-gray-600 mb-1">Severity</p>
          <Select
            className="w-full"
            value={severity}
            onChange={setSeverity}
            options={SEVERITIES.map((s) => ({ value: s, label: s }))}
          />
        </div>
      </div>
      <p className="text-xs font-semibold text-gray-600 mb-1">
        Describe the discrepancy to detect
      </p>
      <TextArea
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        placeholder="e.g. flag completed visits that fall outside their scheduled protocol window"
        autoSize={{ minRows: 3, maxRows: 6 }}
        maxLength={4000}
      />
      <p className="text-[11px] text-gray-400 mt-1.5">
        The rule name and domain are drafted from this description — you can
        edit both before saving. Examples: {PROMPT_EXAMPLES[0]};{" "}
        {PROMPT_EXAMPLES[1]}.
      </p>
      {errorText && step === 0 && (
        <Alert className="mt-3" type="error" showIcon message={errorText} />
      )}
      <div className="flex justify-end items-center gap-3 mt-5">
        <Button
          type="text"
          className="text-gray-500"
          onClick={() => {
            setStep(1);
            setErrorText(null);
          }}
        >
          Skip — write SQL directly
        </Button>
        <Button
          type="primary"
          loading={generateMutation.isPending}
          disabled={!prompt.trim()}
          onClick={() => generateMutation.mutate()}
        >
          Generate SQL logic
        </Button>
      </div>
    </div>
  );

  const logicStep = (
    <div>
      <div className="grid grid-cols-2 gap-3 mb-3 max-w-[860px]">
        <div>
          <p className="text-xs font-semibold text-gray-600 mb-1">
            Rule name <span className="font-normal text-gray-400">(draft)</span>
          </p>
          <Input
            data-testid="rule-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Drafted from your description — edit as needed"
            maxLength={300}
          />
        </div>
        <div>
          <p className="text-xs font-semibold text-gray-600 mb-1">
            Domain <span className="font-normal text-gray-400">(inferred)</span>
          </p>
          <Select
            className="w-full"
            value={domain}
            onChange={setDomain}
            options={DOMAINS.map((d) => ({ value: d, label: d }))}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 items-start">
        <div>
          <div className="border border-gray-200 rounded-md overflow-hidden">
            <div className="flex justify-between items-center px-3 py-1.5 bg-gray-50 border-b border-gray-200 text-[11px] text-gray-500">
              <span>
                Rule logic — SQL · v{sqlVersion}
                {resultsStale ? " · edited" : ""}{" "}
                <span className="text-gray-400">(editable)</span>
              </span>
              {tables.length > 0 && (
                <span className="font-mono">{tables.join(" · ")}</span>
              )}
            </div>
            <TextArea
              data-testid="rule-sql-editor"
              value={sql}
              onChange={(e) => setSql(e.target.value)}
              spellCheck={false}
              className="font-mono !border-none !shadow-none"
              style={{ fontFamily: "monospace", fontSize: 12 }}
              autoSize={{ minRows: 12, maxRows: 20 }}
              placeholder="-- Write a single SELECT returning natural_key, detail_text (and subject_id for participant-level rules)"
            />
          </div>
          {explanation && (
            <div className="mt-2 rounded-md border border-[#BFDCEA] bg-[#EAF4F9] px-3 py-2 text-xs text-[#0B4A66]">
              <span className="font-semibold text-[#06709B]">
                How this works:
              </span>{" "}
              {explanation}
            </div>
          )}
          <div className="flex items-center gap-2 mt-2">
            <Input
              value={refinement}
              onChange={(e) => setRefinement(e.target.value)}
              placeholder="Refine with AI — e.g. exclude screen failures…"
              onPressEnter={() => refinement.trim() && refineMutation.mutate()}
            />
            <Button
              size="small"
              loading={refineMutation.isPending}
              disabled={!refinement.trim() || !sql.trim()}
              onClick={() => refineMutation.mutate()}
            >
              Refine
            </Button>
          </div>
        </div>
        <div>
          <div className="flex justify-between items-start mb-2">
            <div>
              <p
                className="text-base font-semibold text-[#003832]"
                data-testid="rule-test-count"
              >
                {testResult
                  ? `${testResult.total_count} matching record${
                      testResult.total_count === 1 ? "" : "s"
                    }`
                  : "Not tested yet"}
              </p>
              <p className="text-[11px] text-gray-400">
                {testResult
                  ? `The exact rows this rule flags against current trial data · ${testResult.duration_ms} ms · read-only`
                  : "Run a test to see every record this rule would flag today."}
              </p>
            </div>
            <Button
              type="primary"
              loading={testMutation.isPending}
              disabled={!sql.trim()}
              onClick={() => testMutation.mutate()}
            >
              ▶ Run test
            </Button>
          </div>
          {resultsStale && (
            <Alert
              className="mb-2"
              type="warning"
              showIcon
              message="SQL changed since the last test — results below are stale."
              action={
                <Button size="small" onClick={() => testMutation.mutate()}>
                  Re-run test
                </Button>
              }
            />
          )}
          {errorText && step === 1 && (
            <Alert
              className="mb-2"
              type="error"
              showIcon
              message={errorText}
              closable
              onClose={() => setErrorText(null)}
            />
          )}
          {testResult ? (
            <>
              {testResult.truncated && (
                <p className="text-[11px] text-gray-400 mb-1">
                  Showing the first {testResult.row_cap} of{" "}
                  {testResult.total_count} records.
                </p>
              )}
              <Table
                size="small"
                rowKey={(_, index) => String(index)}
                columns={resultColumns}
                dataSource={testResult.rows as never[]}
                pagination={false}
                scroll={{ x: true, y: 320 }}
              />
            </>
          ) : (
            <div className="border border-gray-200 rounded-md py-10 text-center text-gray-400 text-sm">
              No test run yet
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const reviewStep = (
    <div className="grid grid-cols-2 gap-4 items-start">
      <div className="border border-gray-200 rounded-md p-4">
        <p className="text-sm font-semibold text-[#003832] mb-3">{name}</p>
        <dl className="grid grid-cols-[130px_1fr] gap-y-1.5 text-xs">
          <dt className="text-gray-400">Level</dt>
          <dd>{level}</dd>
          <dt className="text-gray-400">Domain</dt>
          <dd>{domain}</dd>
          <dt className="text-gray-400">Severity</dt>
          <dd>{severity}</dd>
          <dt className="text-gray-400">Logic</dt>
          <dd>SQL v{sqlVersion} — locked on activation</dd>
          <dt className="text-gray-400">Last test</dt>
          <dd>
            {testResult
              ? `${testResult.total_count} matching records · ${testResult.duration_ms} ms`
              : "—"}
          </dd>
          <dt className="text-gray-400">Dedup key</dt>
          <dd className="font-mono text-[11px]">sha1(rule_id · natural_key)</dd>
          <dt className="text-gray-400">Library</dt>
          <dd>Saved to the tenant rule library; other trials can enable it.</dd>
        </dl>
      </div>
      <div className="border border-gray-200 rounded-md p-4 text-xs">
        <p className="text-sm font-semibold text-[#003832] mb-2">
          What happens when this rule is active
        </p>
        <ol className="list-decimal ml-4 space-y-1.5">
          <li>Runs after every data refresh for this trial, and on demand.</li>
          <li>
            Each matching record becomes a discrepancy marked{" "}
            <Tag color="blue">NEW</Tag>, status Open, unassigned.
          </li>
          <li>
            Records already flagged are never duplicated — identity comes from
            the dedup key.
          </li>
          <li>
            If a record stops matching after a refresh, its discrepancy
            auto-resolves as “No longer detected”.
          </li>
          <li>
            Assignment, status changes, and comments are never overwritten by
            re-runs.
          </li>
        </ol>
        {errorText && step === 2 && (
          <Alert className="mt-3" type="error" showIcon message={errorText} />
        )}
      </div>
    </div>
  );

  return (
    <>
      {contextHolder}
      <Modal
        title="New discrepancy rule"
        open={open}
        onCancel={handleClose}
        width={step === 0 ? 820 : 1080}
        destroyOnClose
        footer={
          step === 0 ? null : (
            <div className="flex justify-between">
              <Button onClick={() => setStep(step - 1)}>‹ Back</Button>
              {step === 1 ? (
                <Tooltip
                  title={
                    hasFreshTest && name.trim()
                      ? ""
                      : "Run a passing test and set a rule name before continuing"
                  }
                >
                  <Button
                    type="primary"
                    disabled={!hasFreshTest || !name.trim()}
                    onClick={() => setStep(2)}
                  >
                    Continue ›
                  </Button>
                </Tooltip>
              ) : (
                <div className="flex gap-2">
                  <Button
                    loading={
                      saveMutation.isPending && saveMutation.variables === false
                    }
                    onClick={() => saveMutation.mutate(false)}
                  >
                    Save as draft
                  </Button>
                  <Button
                    type="primary"
                    loading={
                      saveMutation.isPending && saveMutation.variables === true
                    }
                    onClick={() => saveMutation.mutate(true)}
                  >
                    Activate rule
                  </Button>
                </div>
              )}
            </div>
          )
        }
      >
        <Steps
          size="small"
          current={step}
          className="mb-5 max-w-[640px]"
          items={[
            { title: "Describe" },
            { title: "Logic & test" },
            { title: "Review & activate" },
          ]}
        />
        {step === 0 && describeStep}
        {step === 1 && logicStep}
        {step === 2 && reviewStep}
      </Modal>
    </>
  );
}
